export type VersionItem = {
  id: string;
  tab: string;
  name: string;
  tagline: string;
  description: string;
  features: string[];
  bestFor: string;
  badge: string;
};

export const versionTabs = [
  { id: "cn", label: "中文版" },
  { id: "mandarin", label: "国语版" },
  { id: "subtitle", label: "中文字幕" },
  { id: "online", label: "字幕在线" },
];

export const versionsData: VersionItem[] = [
  {
    id: "cn",
    tab: "cn",
    name: "善良的女秘书 中文版",
    tagline: "以中文字幕为底，配合中文界面说明",
    description:
      "中文版指以中文作为主要阅读语言的整理方式：在保留作品原有声画的前提下，把对白、旁白与画面文字统一整理为中文表述，方便不熟悉原文的观众快速进入情节。",
    features: [
      "对白、旁白、画面文字均为中文呈现",
      "术语与人名保持全篇统一译法",
      "可按集数顺序连续浏览，无需来回切换",
    ],
    bestFor: "第一次接触该作品、希望一口气看完整段情节的观众",
    badge: "综合首选",
  },
  {
    id: "mandarin",
    tab: "mandarin",
    name: "善良的女秘书 国语版",
    tagline: "以国语配音思路整理的译制版本",
    description:
      "国语版更强调口语化的中文表达：在语气、称谓与日常用词上贴近国语习惯，读起来像日常对话，而不是逐字直译的书面语。",
    features: [
      "口语化台词，语气更自然",
      "称谓与敬语按中文语境调整",
      "适合边听边读、跟读练习",
    ],
    bestFor: "习惯国语语感、希望台词顺口的观众",
    badge: "顺口易读",
  },
  {
    id: "subtitle",
    tab: "subtitle",
    name: "善良的女秘书 中文字幕",
    tagline: "逐句对应的中文字幕文件说明",
    description:
      "中文字幕关注的是「逐句对应」：每一句原文都有对应的中文译文，并按时间轴排序，便于在播放时同步显示。此处介绍字幕文件的常见字段与校对要点。",
    features: [
      "一句原文对应一句译文，时间轴对齐",
      "标注说话人、语气词与场景提示",
      "常用 srt / ass 等通用字幕格式",
    ],
    bestFor: "需要把字幕文件与画面同步播放的观众",
    badge: "专业向",
  },
  {
    id: "online",
    tab: "online",
    name: "善良的女秘书 字幕在线",
    tagline: "在线查看字幕内容与版本说明",
    description:
      "字幕在线指无需下载文件，直接在网页中查看字幕文本与版本说明。适合先快速浏览译文风格，再决定使用哪一个版本继续观看。",
    features: [
      "无需安装播放器即可查看文本",
      "可对照不同版本比较译文",
      "支持关键词检索与段落定位",
    ],
    bestFor: "想先试读译文、再决定版本的观众",
    badge: "即开即读",
  },
];

export type CompareRow = {
  dimension: string;
  cn: string;
  mandarin: string;
  subtitle: string;
  online: string;
};

export const compareRows: CompareRow[] = [
  {
    dimension: "主要用途",
    cn: "整体理解情节",
    mandarin: "体会顺口台词",
    subtitle: "逐句对照播放",
    online: "快速试读译文",
  },
  {
    dimension: "语言风格",
    cn: "规范书面表达",
    mandarin: "口语化、贴近日常",
    subtitle: "忠实对应原文",
    online: "以阅读为主",
  },
  {
    dimension: "呈现形式",
    cn: "中文文本整理",
    mandarin: "国语语感台词",
    subtitle: "带时间轴的字幕文件",
    online: "网页内文本查看",
  },
  {
    dimension: "是否需下载",
    cn: "不需要",
    mandarin: "不需要",
    subtitle: "通常需要字幕文件",
    online: "完全不需要",
  },
  {
    dimension: "适合人群",
    cn: "新观众",
    mandarin: "偏爱国语语感者",
    subtitle: "字幕整理与校对者",
    online: "先试读再决定者",
  },
];

export type EpisodeItem = {
  id: number;
  title: string;
  synopsis: string;
  runtime: string;
  translator: string;
  status: "已校对" | "译制中" | "待校对";
  category: "正片" | "特辑" | "花絮";
  updated: string;
};

export const episodeList: EpisodeItem[] = [
  {
    id: 1,
    title: "第 1 集 · 初次登场的日程表",
    synopsis:
      "故事从一份排得密不透风的日程开始，女秘书用她的细致把混乱的早晨重新排回秩序，也由此带出她待人处事的基本方式。",
    runtime: "24 分钟",
    translator: "晨光字幕组",
    status: "已校对",
    category: "正片",
    updated: "2026-09-18",
  },
  {
    id: 2,
    title: "第 2 集 · 一通被误会的电话",
    synopsis:
      "一通被转错的电话引出误会，她在解释与沉默之间选择了更温和的处理方式，字幕里保留了原句的语气词以便对照。",
    runtime: "25 分钟",
    translator: "晨光字幕组",
    status: "已校对",
    category: "正片",
    updated: "2026-09-20",
  },
  {
    id: 3,
    title: "第 3 集 · 抽屉里的备用伞",
    synopsis:
      "一场突如其来的雨让办公室里的关系有了微妙变化，备用伞成了整集反复出现的线索，译文中统一了相关称谓。",
    runtime: "24 分钟",
    translator: "青柠译制",
    status: "已校对",
    category: "正片",
    updated: "2026-09-23",
  },
  {
    id: 4,
    title: "第 4 集 · 会议室里的二十分钟",
    synopsis:
      "一场短会把矛盾集中呈现，台词密集、语速偏快，本章字幕特别标注了说话人与场景提示，方便跟读。",
    runtime: "26 分钟",
    translator: "青柠译制",
    status: "译制中",
    category: "正片",
    updated: "2026-09-27",
  },
  {
    id: 5,
    title: "第 5 集 · 加班的理由",
    synopsis:
      "加班这件事被讲了三种版本，谁在说真话成为本集的趣味所在，译文保留了原作中重复出现的口头禅。",
    runtime: "24 分钟",
    translator: "南岸译社",
    status: "已校对",
    category: "正片",
    updated: "2026-09-30",
  },
  {
    id: 6,
    title: "第 6 集 · 手写的便条",
    synopsis:
      "一张手写便条推进了整条线索，画面文字较多，本章字幕把出现的书面文字一并译出并标注位置。",
    runtime: "25 分钟",
    translator: "南岸译社",
    status: "待校对",
    category: "正片",
    updated: "2026-10-02",
  },
  {
    id: 7,
    title: "特辑 · 译制幕后的一周",
    synopsis:
      "记录字幕组一周的校对流程：从听写、初翻、术语统一到时间轴复核，适合想了解中字制作过程的观众。",
    runtime: "18 分钟",
    translator: "联合校订",
    status: "已校对",
    category: "特辑",
    updated: "2026-09-25",
  },
  {
    id: 8,
    title: "花絮 · 称谓与敬语的取舍",
    synopsis:
      "围绕国语版中「您/你」的取舍展开讨论，举例说明同一句台词在不同语境下的不同译法及其原因。",
    runtime: "12 分钟",
    translator: "联合校订",
    status: "已校对",
    category: "花絮",
    updated: "2026-10-01",
  },
];

export const episodeCategories = ["全部", "正片", "特辑", "花絮"] as const;

export type GroupItem = {
  name: string;
  focus: string;
  style: string;
  note: string;
  works: number;
};

export const subtitleGroups: GroupItem[] = [
  {
    name: "晨光字幕组",
    focus: "对白完整性",
    style: "尽量保留原句结构与语气词",
    note: "适合想逐句核对原文语序的观众，译文偏严谨。",
    works: 12,
  },
  {
    name: "青柠译制",
    focus: "口语顺读",
    style: "把长句拆短，读起来更接近日常对话",
    note: "适合以连续观看为主的观众，节奏轻快。",
    works: 9,
  },
  {
    name: "南岸译社",
    focus: "术语统一",
    style: "人名、称谓、专有词全篇统一",
    note: "适合在意前后一致性的观众，整季体验连贯。",
    works: 15,
  },
  {
    name: "联合校订",
    focus: "双语对照",
    style: "中文为主，关键句附原文提示",
    note: "适合既想看中文又想知道原句表达的观众。",
    works: 7,
  },
];

export type GuideStep = {
  step: string;
  title: string;
  detail: string;
};

export const watchGuide: GuideStep[] = [
  {
    step: "01",
    title: "先确认想要哪一种版本",
    detail:
      "只想看懂情节就选中文版；在意台词顺口就选国语版；需要与画面同步播放则准备中文字幕文件；只是想先看看译文风格，直接用字幕在线浏览即可。",
  },
  {
    step: "02",
    title: "对照分集状态再决定顺序",
    detail:
      "分集列表中标注了各集的校对状态。建议先看「已校对」的章节，遇到「译制中」的章节可稍后再看，避免中途因译文调整而影响体验。",
  },
  {
    step: "03",
    title: "核对译名与称谓是否统一",
    detail:
      "不同字幕组对人名与称谓的处理可能不同。若你前后混用多个版本，建议固定一个译名表，读起来才不会混乱。",
  },
  {
    step: "04",
    title: "善用检索与段落定位",
    detail:
      "想回看某句台词时，可用关键词在字幕在线页面检索，再按段落定位到对应位置，比反复拖动进度更省时间。",
  },
];

export type GlossaryItem = {
  term: string;
  meaning: string;
};

export const glossary: GlossaryItem[] = [
  { term: "中字", meaning: "仅为「中文字幕」的简称，指提供中文译文的字幕版本。" },
  { term: "时间轴", meaning: "标出每句字幕出现与消失时间点的信息，是字幕与画面对齐的关键。" },
  { term: "校对", meaning: "在初翻之后复核译文准确性与用词统一的过程。" },
  { term: "硬字幕", meaning: "直接压制在画面上的字幕，无法单独关闭或调整。" },
  { term: "软字幕", meaning: "可作为独立轨道开关的字幕，通常以文件形式提供。" },
  { term: "译名表", meaning: "全篇统一使用的专有名词与人名对照清单。" },
];

export type FaqItem = {
  question: string;
  answer: string;
};

export const faqList: FaqItem[] = [
  {
    question: "「中文字幕」和「国语版」到底有什么区别？",
    answer:
      "两者都以中文呈现。区别在于侧重：中文字幕强调逐句对应原文、按时间轴同步；国语版则更注重口语顺读，把语气和称谓调整得更接近日常国语表达。想看细节选前者，想读得顺口选后者。",
  },
  {
    question: "「字幕在线」需要下载或安装播放器吗？",
    answer:
      "不需要。字幕在线指直接在网页中查看字幕文本与版本说明，适合先快速试读译文风格。若要与画面同步播放，再考虑使用带时间轴的字幕文件。",
  },
  {
    question: "不同字幕组的译文可以混着看吗？",
    answer:
      "技术上可以，但不建议。不同组对人名、称谓与专有词的处理可能不同，前后混用容易造成混淆。如果要多版本对照，建议固定同一译名表。",
  },
  {
    question: "分集状态里的「译制中」代表什么？",
    answer:
      "表示该章节的译文仍在整理或等待校对，内容可能后续调整。想获得更稳定的阅读体验，可以优先观看标注为「已校对」的章节。",
  },
  {
    question: "字幕里的「硬字幕」和「软字幕」怎么选？",
    answer:
      "硬字幕已压制在画面上，无法关闭；软字幕是独立轨道，可开关、可调整字体与位置。若你希望灵活控制显示效果，优先选择软字幕形式。",
  },
];

export type UpdateItem = {
  date: string;
  title: string;
  detail: string;
  type: "新增" | "校对" | "调整";
};

export const updateList: UpdateItem[] = [
  {
    date: "2026-10-02",
    title: "第 6 集字幕进入待校对",
    detail: "初翻完成，画面文字已单独标注，等待术语统一与时间轴复核。",
    type: "新增",
  },
  {
    date: "2026-10-01",
    title: "花絮章节补充称谓说明",
    detail: "围绕国语版「您/你」的取舍补充了示例与说明，方便对照阅读。",
    type: "调整",
  },
  {
    date: "2026-09-30",
    title: "第 5 集完成校对",
    detail: "修正了重复口头禅的译法，全章译名与前面章节保持一致。",
    type: "校对",
  },
  {
    date: "2026-09-27",
    title: "第 4 集进入译制流程",
    detail: "台词密集段落增加说话人标注，方便跟读与检索。",
    type: "新增",
  },
  {
    date: "2026-09-25",
    title: "特辑章节上线",
    detail: "新增译制流程记录，覆盖听写、初翻、术语统一与时间轴复核。",
    type: "新增",
  },
];

export const heroStats = [
  { value: "4", label: "版本方向" },
  { value: "8", label: "分集条目" },
  { value: "6", label: "常见问题" },
  { value: "4", label: "字幕组来源" },
];

export const navLinks = [
  { id: "versions", label: "版本速览" },
  { id: "compare", label: "版本对比" },
  { id: "episodes", label: "分集字幕" },
  { id: "groups", label: "字幕组" },
  { id: "guide", label: "观看指南" },
  { id: "faq", label: "常见问题" },
];